import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, EyeOff, Briefcase, Users, Route as RouteIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal, Tilt } from "@/components/Reveal";
import logisticsImg from "@/assets/logistics.jpg";
import expertGuidanceImg from "@/assets/expert-guidance.webp";

export const Route = createFileRoute("/logistics-transport")({
  head: () => ({
    meta: [
      { title: "Logistics & Transport — Comm42 SA" },
      { name: "description", content: "Safe, discreet and reliable mobility — worldwide. VIP transfers, executive travel and international escort convoys." },
      { property: "og:title", content: "Logistics & Transport — Comm42 SA" },
      { property: "og:description", content: "Safe, discreet and reliable mobility — worldwide." },
      { property: "og:url", content: "https://comm42.ch/logistics-transport" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/logistics-transport" },
    ],
  }),
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  const safety = [
    { Icon: ShieldCheck, title: t("logistics.safety.personal.title"), desc: t("logistics.safety.personal.desc") },
    { Icon: EyeOff, title: t("logistics.safety.conf.title"), desc: t("logistics.safety.conf.desc") },
  ];
  const clients = [
    { Icon: Briefcase, title: t("logistics.clients.exec.title"), desc: t("logistics.clients.exec.desc") },
    { Icon: Users, title: t("logistics.clients.vip.title"), desc: t("logistics.clients.vip.desc") },
    { Icon: RouteIcon, title: t("logistics.clients.sensitive.title"), desc: t("logistics.clients.sensitive.desc") },
  ];
  return (
    <PageShell>
      <PageHero
        eyebrow={t("logistics.eyebrow")}
        title={t("logistics.title")}
        subtitle={t("logistics.subtitle")}
      />

      {/* Intro + CTA */}
      <Reveal as="section" className="mx-auto max-w-4xl px-6 py-24 text-center">
        <p className="text-xs tracking-[0.4em] text-primary">{t("logistics.introEyebrow")}</p>
        <h2 className="mt-6 font-serif text-4xl md:text-5xl">{t("logistics.introTitle")}</h2>
        <p className="mx-auto mt-8 max-w-2xl text-muted-foreground leading-relaxed">{t("logistics.introDesc")}</p>
        <Link
          to="/contact"
          className="group mt-10 inline-flex items-center gap-2 bg-foreground px-10 py-3 text-xs tracking-[0.3em] text-background transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-[0_20px_40px_-10px_oklch(0.5_0.09_235/0.4)]"
        >
          {t("logistics.request")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>

      {/* Personal safety — image left, text right */}
      <section className="bg-card/40 py-24">
        <Reveal className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-2 md:items-center">
          <Tilt className="img-zoom border border-border/40">
            <img src={logisticsImg} alt="Private security service" loading="lazy" width={1140} height={684} className="aspect-[1140/684] w-full object-cover" />
          </Tilt>
          <div>
            <p className="text-xs tracking-[0.4em] text-primary">{t("logistics.safetyEyebrow")}</p>
            <h3 className="mt-6 font-serif text-3xl">{t("logistics.safetyTitle")}</h3>
            <ul className="mt-8 space-y-6">
              {safety.map(({ Icon, title, desc }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="mt-1 inline-flex h-10 w-10 flex-none items-center justify-center border border-primary/30 bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <p className="font-semibold text-foreground">{title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* When clients call us — text left, image right */}
      <section className="py-24">
        <Reveal className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-2 md:items-center">
          <div className="md:order-2">
            <Tilt className="img-zoom border border-border/40">
              <img src={expertGuidanceImg} alt="Client logistics" loading="lazy" width={1140} height={684} className="aspect-[1140/684] w-full object-cover" />
            </Tilt>
          </div>
          <div className="md:order-1">
            <p className="text-xs tracking-[0.4em] text-primary">{t("logistics.clientsEyebrow")}</p>
            <h3 className="mt-6 font-serif text-3xl">{t("logistics.clientsTitle")}</h3>
            <ul className="mt-8 space-y-6">
              {clients.map(({ Icon, title, desc }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="mt-1 inline-flex h-10 w-10 flex-none items-center justify-center border border-primary/30 bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <p className="font-semibold text-foreground">{title}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-y border-border/40 bg-card py-24">
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_50%_50%,oklch(0.85_0.12_85),transparent_70%)]" />
        <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl">{t("logistics.ctaTitle")}</h2>
          <p className="mt-6 text-muted-foreground">{t("logistics.ctaDesc")}</p>
          <Link
            to="/contact"
            className="group mt-10 inline-flex items-center gap-2 bg-primary px-10 py-4 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_30px_60px_-15px_oklch(0.5_0.09_235/0.6)]"
          >
            {t("logistics.contact")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </PageShell>
  );
}
